<template>
   <div class="card">
      <div class="card-body m-2">

         <!-- Global Override Alert -->
         <div v-if="settings && settings.block_download == 1"
            class="global-alert alert-block">
            <div class="d-flex align-items-center">
               <i class="fa-solid fa-circle-info me-2" style="font-size: 18px;"></i>
               <div>
                  <strong>Global Setting Active: Block Downloads is set to BLOCK.</strong>
                  <div class="mt-1" style="font-size: 13px; opacity: 0.85;">
                     To manage default file extensions, set "Block Downloads" to "Off" in
                     <router-link to="/account-settings" class="alert-link">Configuration <i class="fa-solid fa-arrow-right mx-1"></i> Settings</router-link>.
                  </div>
               </div>
            </div>
         </div>

         <div class="row p-2 align-items-center">

            <!-- Search + Add Button -->
            <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model.trim="inputExtension" type="text" class="form-control form-control-sm"
                  placeholder="&#xF002; Search or add file extensions..."
                  style="border-radius:8px; height: 40px; font-size: 14px; font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free'; font-weight: 900;"
                  @keyup="onSearch()" v-on:keyup.enter="onEnter" />
            </div>

            <div class="col-12 col-lg-auto d-flex mb-2 mb-lg-0 gap-2">
               <button type="button" class="fw-bold btn btn-danger text-light btn-sm"
                  style="font-size: 14px; height: 40px; border-radius: 8px !important; padding: 0 1.5rem; white-space: nowrap;"
                  @click="addExtension">
                  <i class="fa-solid fa-ban me-1"></i> Block Extension
               </button>
               <button type="button" class="btn btn-outline-secondary btn-sm"
                  style="font-size: 14px; height: 40px; border-radius: 8px !important; padding: 0 1.5rem; white-space: nowrap;"
                  @click="resetToDefaults" :disabled="isResetting">
                  <i class="fa-solid fa-rotate-left me-1"></i> Reset to Defaults
               </button>
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}
                     </option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Info Banner -->
         <div class="row px-2 pb-2">
            <div class="col-12">
               <small class="text-muted">
                  <i class="fa-solid fa-circle-info me-1" style="color: #00A0C1;"></i>
                  This module will only work for Extension Version 4.0.0 and above on Firefox, Chrome, and Edge. Enter file extensions without the dot (e.g. <strong>exe</strong>, <strong>jpg</strong>, <strong>docx</strong>).

               </small>
            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col">File Extension</th>
                     <th scope="col" class="text-center">Actions</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0 && !isLoading">
                  <tr>
                     <td colspan="2">No blocked file extensions. Add one above or reset to defaults.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(ext, index) in blockedExtensions" :key="index">
                     <td>
                        <i class="fa-solid fa-file-shield me-2" style="color: #dc3545;"></i>
                        <strong>.{{ ext.extension }}</strong>
                     </td>
                     <td class="text-center">
                        <button type="button" class="ms-action-btn ms-action-btn--delete"
                            @click="deleteExtension(index)"
                            :disabled="settings && settings.block_download == 1 && isDefaultExtension(ext.extension)"
                            :title="settings && settings.block_download == 1 && isDefaultExtension(ext.extension) ? 'Cannot delete default extensions while Block Downloads is enabled' : 'Remove from blocked extensions'">
                           <i class="fa fa-xmark"></i>
                        </button>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>
      </div>
   </div>
</template>

<script>
import { debounce, extractErrorMessage } from '@/plugins/helper.js'
export default {

   props: {
      settings: {
         type: Object,
         default: () => null
      }
   },

   data() {
      return {
         debounceFn: null,
         blockedExtensions: [],
         inputExtension: "",
         isLoading: false,
         isResetting: false,
         paginationData: {},
         pagination: {
            pageSize: 50,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "Extension: A-Z", value: "extension__asc" },
            { text: "Extension: Z-A", value: "extension__desc" },
            { text: "Date: Newest", value: "date_created__desc" },
            { text: "Date: Oldest", value: "date_created__asc" },
         ],
         sortBy: "extension__asc",
         sorting: {
            field: "extension",
            order: "asc"
         },
      };
   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },

      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() {
         this.getBlockedExtensions()
      },

      rowsPerPage() {
         this.pagination.page = 1
         this.getBlockedExtensions()
      },

      sortItems() {
         if (this.sortBy.includes("__")) {
            let sort = this.sortBy.split("__")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getBlockedExtensions()
      },
   },

   mounted() {
      this.init();
   },

   created() {
      this.debounceFn = debounce(() =>
         this.getBlockedExtensions()
         , 500)
   },

   methods: {

      isDefaultExtension(ext) {
         const defaults = ['exe', 'bat', 'ps1', 'dmg', 'msi', 'vbs', 'pkg'];
         return defaults.includes(ext.toLowerCase());
      },

      init() {
         this.getBlockedExtensions()
      },

      onSearch() {
         this.debounceFn()
      },

      previousPage() {
         this.pagination.page > 1 ? this.pagination.page -= 1 : ''
      },

      nextPage() {
         this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : ''
      },

      onEnter: function () {
         this.addExtension()
      },

      async addExtension() {
         try {
            if (this.inputExtension == null || this.inputExtension.trim() == "") {
               this.$toast.error("Please provide a valid file extension.")
               return
            }

            // Normalize: strip leading dot, lowercase
            let ext = this.inputExtension.replace(/^\./, '').toLowerCase().trim()

            // Validate: alphanumeric only, max 20 chars
            if (!/^[a-zA-Z0-9]+$/.test(ext) || ext.length > 20) {
               this.$toast.error("File extension must be alphanumeric (max 20 characters).")
               return
            }

            const payload = {
               extension: ext
            }

            const resp = await this.$axios.post("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/add-account-blocked-file-extension", payload)

            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message + " Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.")
            }

            this.inputExtension = ""
            this.getBlockedExtensions()

         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to add file extension."))
            this.inputExtension = ""
            this.getBlockedExtensions()
         }
      },

      async getBlockedExtensions() {

         let apiPath = "extension-configuration/" + this.$store.state.auth.organisation_uuid + "/get-account-blocked-file-extensions?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page

         if (this.sorting.order != "" && this.sorting.field != "") {
            apiPath += "&sort_by=" + this.sorting.field
            apiPath += "&order_by=" + this.sorting.order
         }

         if (this.inputExtension !== "" || this.inputExtension !== null) {
            apiPath += "&search_key=" + this.inputExtension
         }

         this.isLoading = true
         await this.$axios.get(apiPath).
            then((response) => {
               if (response.data.blocked_file_extensions === "No Record Found")
                  this.blockedExtensions = []
               else {
                  this.blockedExtensions = response.data.blocked_file_extensions
               }

               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               console.log(error)
               this.$toast.error(extractErrorMessage(error, "Failed to load blocked file extensions."))
            })
            .finally(() => {
               this.isLoading = false
            })

      },

      async deleteExtension(index) {
         try {
            const resp = await this.$axios.delete("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/remove-account-blocked-file-extension/" + this.blockedExtensions[index].id)

            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message + " Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.")
               this.getBlockedExtensions()
            }

         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to remove file extension."))
         }
      },

      async resetToDefaults() {
         try {
            this.isResetting = true
            const resp = await this.$axios.post("extension-configuration/" + this.$store.state.auth.organisation_uuid + "/reset-account-blocked-file-extensions")

            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message + " Please note: It may take up to 30 minutes for this change to be reflected in your browser. Page refresh is required.")
               this.getBlockedExtensions()
            }
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to reset file extensions to defaults."))
         } finally {
            this.isResetting = false
         }
      }
   }

}

</script>

<style scoped>
/* Uses shared Spark styles: .filterTitle, .filterOptions, .bordered-select,
   .pagination, .pagination-label, .pagination-arrow, .mainTable
   defined in AllowList2.vue */

/* Global alert */
.global-alert {
   padding: 14px 18px;
   border-radius: 10px;
   margin-bottom: 16px;
   font-size: 14px;
}

.global-alert.alert-block {
   background-color: #fef5ed;
   color: #9a6231;
   border: 1px solid #fbe0c4;
}

.global-alert .alert-link {
   color: inherit;
   font-weight: 600;
   text-decoration: underline;
}
</style>
